import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../hooks";
import { DashboardProgressionApi } from "../../api/revi/dashboardProgressionApi";

export function useEvaluationChartsController({
  patientId: initialPatientId,
} = {}) {
  // --- Auth + API instance ---
  const { accessToken } = useAuth();
  const api = useMemo(() => new DashboardProgressionApi(), []);

  // --- Selection state ---
  const [selectedPatientId, setSelectedPatientId] = useState(
    initialPatientId || null,
  );
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [timeRange, setTimeRange] = useState("all");

  // --- Data state ---
  const [evaluationSummary, setEvaluationSummary] = useState([]);
  const [progression, setProgression] = useState({
    history: [],
    pagination: null,
  });
  const [latestResult, setLatestResult] = useState(null);

  // --- Status state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // --- Derived ---
  const isEmpty = !loading && !error && evaluationSummary.length === 0;

  // --- Reset selectedEvaluationId when patient changes ---
  // Use a ref to skip the initial mount so we don't reset unnecessarily on first render
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setSelectedEvaluationId(null);
  }, [selectedPatientId]);

  // --- Load summary (only when patient or timeRange changes) ---
  const loadSummary = useCallback(async () => {
    if (!accessToken || !selectedPatientId) return;

    setLoading(true);
    setError("");

    try {
      const summaryRes = await api.getEvaluationSummary(
        accessToken,
        selectedPatientId,
        { timeRange },
      );
      setEvaluationSummary(summaryRes?.data || []);
    } catch (e) {
      setError(e?.message || "Error cargando datos de progresion");
    } finally {
      setLoading(false);
    }
  }, [accessToken, api, selectedPatientId, timeRange]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // --- Load detail (when selection changes) ---
  const loadDetail = useCallback(async () => {
    if (!accessToken || !selectedPatientId || !selectedEvaluationId) {
      setProgression({ history: [], pagination: null });
      setLatestResult(null);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [historyRes, latestRes] = await Promise.all([
        /*
          Se piden las mas RECIENTES. Con "asc" y el limite de diez por pagina,
          un paciente con seguimiento largo mostraba como estaba hace dos anos y
          habia que paginar para llegar a como esta hoy: exactamente el orden
          contrario al uso clinico.

          Llegan de mas nueva a mas antigua y se les da la vuelta abajo, porque
          una linea temporal se lee de izquierda a derecha.
        */
        api.getPatientHistory(accessToken, selectedPatientId, {
          evaluationId: selectedEvaluationId,
          timeRange,
          sort: "desc",
        }),
        api.getLatestEvaluationResult(
          accessToken,
          selectedPatientId,
          selectedEvaluationId,
        ),
      ]);
      setProgression({
        // De vuelta a orden cronologico para pintar: lo que cambia es CUALES
        // son las diez, no como se leen.
        history: [...(historyRes?.history || [])].reverse(),
        pagination: historyRes?.pagination || null,
      });
      setLatestResult(latestRes?.data || null);
    } catch (e) {
      setError(e?.message || "Error cargando datos de progresion");
    } finally {
      setLoading(false);
    }
  }, [accessToken, api, selectedPatientId, selectedEvaluationId, timeRange]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Combined reload for external callers
  const load = useCallback(async () => {
    await loadSummary();
    await loadDetail();
  }, [loadSummary, loadDetail]);

  return {
    selectedPatientId,
    setSelectedPatientId,
    selectedEvaluationId,
    setSelectedEvaluationId,
    timeRange,
    setTimeRange,
    evaluationSummary,
    progression,
    latestResult,
    loading,
    error,
    isEmpty,
    reload: load,
  };
}
